import { useEffect, useId, useState } from 'react';
import { Lock } from 'lucide-react';
import { Sheet } from './ui';
import { AccountCanceled, AccountError, accountService, type EmailMode, type Provider } from '../lib/account';
import { CaptchaError } from '../lib/captcha';
import { track } from '../lib/analytics';
import { completeSignIn } from '../state/accountSync';
import { useStore } from '../state/store';

/**
 * Asks for an account only when someone does something that needs one (saving a recipe, changing
 * saved preferences). Email plus a 6-digit code; no password. The waiting action runs once they're in.
 */
export function AccountSheet() {
  const prompt = useStore((s) => s.accountPrompt);
  const close = useStore((s) => s.closeAccountPrompt);
  const showToast = useStore((s) => s.showToast);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<EmailMode>('upgrade');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailId = useId();
  const codeId = useId();

  useEffect(() => {
    if (!prompt) return;
    setStep('email'); setCode(''); setError(null); setBusy(false);
    track('account_prompt_shown', { reason: prompt.reason });
  }, [prompt]);

  if (!prompt) return null;
  const service = accountService();

  const send = async () => {
    if (!service) return;
    setBusy(true); setError(null);
    try {
      setMode(await service.sendCode(email.trim()));
      setStep('code');
    } catch (e) {
      setError(e instanceof AccountError || e instanceof CaptchaError ? e.message : 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };
  const verify = async () => {
    if (!service) return;
    setBusy(true); setError(null);
    try {
      const account = await service.verifyCode(email.trim(), code.trim(), mode);
      track('account_created', { mode });
      await completeSignIn(account);
      showToast(mode === 'upgrade' ? 'Account created. You’re all set.' : 'Signed in. Your saved recipes and preferences are here.');
    } catch (e) {
      setError(e instanceof AccountError || e instanceof CaptchaError ? e.message : 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };
  const withProvider = async (provider: Provider) => {
    if (!service) return;
    setBusy(true); setError(null);
    try {
      const result = await service.signInWith(provider);
      if (result === 'redirect') return; // the browser is on its way to Apple or Google and back
      track('account_created', { mode: provider });
      await completeSignIn(result);
      showToast(`Signed in with ${provider === 'apple' ? 'Apple' : 'Google'}. You’re all set.`);
    } catch (e) {
      if (!(e instanceof AccountCanceled)) setError(e instanceof AccountError || e instanceof CaptchaError ? e.message : 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };
  const dismiss = () => { track('account_prompt_dismissed', { step }); close(); };

  return (
    <Sheet open onClose={dismiss} title={step === 'email' ? `Create a free account to ${prompt.reason}` : 'Check your email'}>
      {step === 'email' ? (
        <form onSubmit={(e) => { e.preventDefault(); void send(); }}>
          <p className="text-body text-fg-muted mb-4">Your saved recipes and preferences stay with you on any device. Already have an account? Sign in the same way.</p>
          <button type="button" className="btn w-full bg-fg text-bg mb-2" disabled={busy} onClick={() => void withProvider('apple')}>
            <svg width="17" height="20" viewBox="0 0 17 20" aria-hidden fill="currentColor"><path d="M14.1 10.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9 0 0-2.7-1-2.7-4.1zM11.6 3c.7-.9 1.2-2 1.1-3.2-1 0-2.3.7-3 1.6-.7.8-1.2 2-1.1 3.1 1.1.1 2.3-.6 3-1.5z" /></svg>
            Continue with Apple
          </button>
          <button type="button" className="btn w-full bg-surface text-fg border border-line mb-3" disabled={busy} onClick={() => void withProvider('google')}>
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.7 1.2 9.2 3.6l6.9-6.9C35.9 2.4 30.5 0 24 0 14.6 0 6.6 5.4 2.6 13.3l8 6.2C12.5 13.6 17.8 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 7l7.5 5.8c4.4-4 6.8-10 6.8-17.3z" />
              <path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.1.8-4.6l-8-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.9-6.2z" />
              <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.2 0-11.5-4.2-13.4-9.9l-8 6.2C6.6 42.6 14.6 48 24 48z" />
            </svg>
            Continue with Google
          </button>
          <p className="text-center text-footnote text-fg-muted mb-3">or use your email</p>
          <label htmlFor={emailId} className="block text-subhead font-semibold mb-1.5">Email</label>
          <input id={emailId} className="input w-full mb-3" type="email" inputMode="email" autoComplete="email" required maxLength={254}
            value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          {error && <p role="alert" className="text-subhead text-danger mb-3">{error}</p>}
          <button className="btn btn-primary w-full" type="submit" disabled={busy || !email.includes('@')}>{busy ? 'Sending…' : 'Continue with email'}</button>
          <button className="btn btn-ghost w-full mt-1" type="button" onClick={dismiss}>Not now</button>
          <p className="flex gap-2 items-start text-footnote text-fg-muted mt-3"><Lock size={14} className="shrink-0 mt-0.5" aria-hidden />No password. With email, we send you a 6-digit code. We never share your email.</p>
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void verify(); }}>
          <p className="text-body text-fg-muted mb-4">We sent a 6-digit code to <strong className="text-fg">{email.trim()}</strong>.</p>
          <label htmlFor={codeId} className="block text-subhead font-semibold mb-1.5">Code</label>
          <input id={codeId} className="input w-full mb-3 tabular tracking-[0.3em]" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          {error && <p role="alert" className="text-subhead text-danger mb-3">{error}</p>}
          <button className="btn btn-primary w-full" type="submit" disabled={busy || code.length !== 6}>{busy ? 'Checking…' : 'Confirm'}</button>
          <button className="btn btn-ghost w-full mt-1" type="button" onClick={() => { setStep('email'); setError(null); }}>Use a different email</button>
        </form>
      )}
    </Sheet>
  );
}
